/**
 * Centralized React Query Configuration
 * Provides consistent caching, retry, and stale time settings across the application
 */

export const queryConfig = {
  /**
   * Stale time configuration (how long data is considered fresh)
   * After stale time expires, data can be refetched in the background
   */
  staleTime: {
    metadata: 5 * 60 * 1000, // 5 minutes - DocType schemas change rarely
    list: 30 * 1000, // 30 seconds - List data changes more frequently
    detail: 60 * 1000, // 1 minute - Detail views can be cached longer
    search: 10 * 1000, // 10 seconds - Search results should be fresh
  },

  /**
   * Cache time configuration (how long unused data stays in cache)
   * After cache time expires, unused data is garbage collected
   */
  cacheTime: {
    default: 5 * 60 * 1000, // 5 minutes
    metadata: 10 * 60 * 1000, // 10 minutes - Metadata rarely changes
  },

  /**
   * Retry configuration
   */
  retry: {
    default: 2, // Retry failed requests 2 times
    networkError: 3, // Retry network errors 3 times
    serverError: 1, // Retry server errors (5xx) only once
    clientError: 0, // Don't retry client errors (4xx)
  },

  /**
   * Retry delay configuration (exponential backoff)
   */
  retryDelay: (attemptIndex: number) => {
    // Exponential backoff: 1s, 2s, 4s
    return Math.min(1000 * 2 ** attemptIndex, 30000);
  },

  /**
   * Refetch configuration
   */
  refetchOnWindowFocus: true, // Refetch when window regains focus
  refetchOnReconnect: true, // Refetch when network reconnects
  refetchOnMount: true, // Refetch on mount if data is stale (React Query default)
};

/**
 * Get query options for a specific query type
 */
export function getQueryOptions(type: keyof typeof queryConfig.staleTime) {
  return {
    staleTime: queryConfig.staleTime[type],
    cacheTime: queryConfig.cacheTime.default,
    retry: queryConfig.retry.default,
    retryDelay: queryConfig.retryDelay,
    refetchOnWindowFocus: queryConfig.refetchOnWindowFocus,
    refetchOnReconnect: queryConfig.refetchOnReconnect,
    refetchOnMount: queryConfig.refetchOnMount,
  };
}

